import React, { CSSProperties, useContext, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  Empty,
  Result,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography,
} from 'antd';

import {
  GetClientOrganizationDetailsQuery,
  useGetClientOrganizationDetailsQuery,
} from '../graphql/generated';
import { UserContext } from '../contexts/UserContext';
import { CMSTables } from '../components/networks/NetworkDescription';
import { compareClientNetworks } from './clientNetworkSort';

type OrganizationNetwork = NonNullable<
  NonNullable<
    NonNullable<
      GetClientOrganizationDetailsQuery['users_by_pk']
    >['client_organization']
  >['organization_networks']
>[0];

type UploadOrganizationNetwork = OrganizationNetwork & {
  network: OrganizationNetwork['network'] & {
    dc_id: string;
    network_number: number;
  };
};

const pageStyle: CSSProperties = {
  margin: '0 auto',
  maxWidth: 1440,
};

const contentCardStyle: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
  marginTop: 16,
};

const ClientFileUploadsPage: React.FC = () => {
  const { user } = useContext(UserContext);
  const { data, loading, error } = useGetClientOrganizationDetailsQuery({
    variables: { user_id: user?.user_id },
  });
  const organization = data?.users_by_pk?.client_organization;

  const uploadNetworks = useMemo(() => {
    const organizationNetworks = organization?.organization_networks || [];

    return organizationNetworks
      .filter(
        (
          organizationNetwork
        ): organizationNetwork is UploadOrganizationNetwork => {
          return (
            !organizationNetwork.is_archived &&
            !organizationNetwork.network.is_archived &&
            Boolean(organizationNetwork.network.dc_id) &&
            typeof organizationNetwork.network.network_number === 'number'
          );
        }
      )
      .sort((a, b) => compareClientNetworks(a.network, b.network));
  }, [organization]);

  if (loading) return <Skeleton />;
  if (error)
    return (
      <Result
        status="error"
        title="There was an error retrieving file upload details"
        subTitle={error?.message}
      />
    );
  if (!organization)
    return (
      <Result
        status="warning"
        title="No organization profile found"
        subTitle="Your account is not attached to a client organization."
      />
    );

  return (
    <>
      <Helmet>
        <title>File Uploads | Mabel</title>
      </Helmet>
      <div style={pageStyle}>
        <Typography.Title level={2} style={{ marginBottom: 4 }}>
          File Uploads
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ fontSize: 16 }}>
          Upload CMS files required for BNL and SVA processing.
        </Typography.Paragraph>

        {uploadNetworks.length ? (
          <Tabs>
            {uploadNetworks.map(({ network }) => (
              <Tabs.TabPane
                tab={
                  <Space size={8}>
                    <span>{network.network_name}</span>
                    <Tag>{network.dc_id}</Tag>
                  </Space>
                }
                key={network.network_id}
              >
                <Card bodyStyle={{ padding: 24 }} style={contentCardStyle}>
                  <CMSTables
                    dc_id={network.dc_id}
                    includeHedrTables={false}
                    clientOrganizationId={organization.organization_id}
                    network_id={network.network_id}
                    network_number={network.network_number}
                    showDeleteActions={false}
                  />
                </Card>
              </Tabs.TabPane>
            ))}
          </Tabs>
        ) : (
          <Card style={contentCardStyle}>
            <Empty description="No active networks with ACO IDs are attached to this organization." />
          </Card>
        )}
      </div>
    </>
  );
};

export default ClientFileUploadsPage;
